import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, LayoutDashboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { createLogger } from '@/lib/logger';

interface Props {
  children: ReactNode;
  /** Optional context for more precise error messages (e.g. "Patientenakte"). */
  context?: string;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

const log = createLogger('ErrorBoundary');

/**
 * Error Boundary for healthcare context.
 *
 * Important: never show stack traces or technical details in the UI.
 *  - Prevents information leaks to users
 *  - Error ID for support without exposing sensitive data
 *  - No reload of the entire app — only the affected area falls out
 *
 * The fallback UI lives in `<ErrorFallback />` so it can use the
 * `useTranslation` hook (class components can't).
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(): State {
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, errorId };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Only log to console; never propagate raw error messages to the user.
    log.error('unhandled error', error, {
      errorId: this.state.errorId,
      context: this.props.context,
      componentStack: info.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, errorId: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleNavigateDashboard = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          context={this.props.context}
          errorId={this.state.errorId}
          onReload={this.handleReload}
          onNavigateDashboard={this.handleNavigateDashboard}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}

interface ErrorFallbackProps {
  context: string | undefined;
  errorId: string | null;
  onReload: () => void;
  onNavigateDashboard: () => void;
  onReset: () => void;
}

/**
 * Function-component fallback so we can use the `useTranslation` hook.
 * The class boundary above just delegates to this on error.
 */
function ErrorFallback({
  context,
  errorId,
  onReload,
  onNavigateDashboard,
  onReset,
}: ErrorFallbackProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="w-full max-w-sm space-y-5">
        {/* Icon */}
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>

        {/* Message */}
        <div className="space-y-2">
          <h2 className="text-lg font-semibold text-foreground">
            {context
              ? t('errorBoundary.titleWithContext', { context })
              : t('errorBoundary.title')}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t('errorBoundary.body')}
          </p>
        </div>

        {/* Error ID */}
        <div className="rounded-lg border bg-muted/50 px-4 py-2.5">
          <p className="text-xs text-muted-foreground mb-0.5">
            {t('errorBoundary.errorRefLabel')}
          </p>
          <p className="text-sm font-mono font-medium text-foreground">{errorId}</p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2">
          <Button onClick={onReload} className="w-full">
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('errorBoundary.reload')}
          </Button>
          <Button variant="outline" onClick={onNavigateDashboard} className="w-full">
            <LayoutDashboard className="w-4 h-4 mr-2" />
            {t('errorBoundary.backToDashboard')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="w-full text-muted-foreground"
          >
            {t('errorBoundary.tryAgain')}
          </Button>
        </div>
      </div>
    </div>
  );
}
